/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { useSlidingStrip } from '@/shared/hooks/use-sliding-strip';
import { Text, UnstyledButton } from '@/shared/ui';
import type { ClientPerformance } from '../../hooks/useClientPerformance';
import type { ClientDetailTab } from './client-detail-tabs';

export interface ClientPerformanceStripProps {
  /** Read once by the shell, so the strip and the tab counts agree. */
  perf: ClientPerformance;
  onOpenTab: (tab: ClientDetailTab) => void;
}

/** One figure in the band: what it counts, and the tab that details it. */
interface Figure {
  id: string;
  label: string;
  value: string;
  unit: string;
  tab: ClientDetailTab;
}

/**
 * One figure, rendered as a button because every one of them answers
 * "where do I see these?" by opening the tab that holds them.
 */
function FigureButton({ figure, onOpenTab, wasDragged }: {
  figure: Figure;
  onOpenTab: (tab: ClientDetailTab) => void;
  /** Dragging the strip past a figure must not open its tab on release. */
  wasDragged?: () => boolean;
}) {
  return (
    <UnstyledButton
      type="button"
      data-testid={`client-figure-${figure.id}`}
      onClickCapture={(e) => { if (wasDragged?.()) { e.preventDefault(); e.stopPropagation(); } }}
      onClick={() => onOpenTab(figure.tab)}
      className="flex min-w-0 flex-none flex-col items-start gap-[3px] border-none bg-transparent p-0 text-left cursor-pointer max-md:min-w-[110px]"
    >
      <Text
        component="span"
        className="text-[length:var(--text-meta)] leading-[var(--leading-snug)] text-(--text-tertiary-soft)"
      >
        {figure.label}
      </Text>
      <Text component="span" className="flex items-baseline gap-[4px]">
        <Text
          component="span"
          className="text-[length:var(--text-stat)] [font-weight:var(--font-bold)] leading-none tabular-nums text-(--seed-base)"
        >
          {figure.value}
        </Text>
        {figure.unit && (
          <Text component="span" className="text-[length:var(--text-meta)] text-(--text-tertiary-soft)">
            {figure.unit}
          </Text>
        )}
      </Text>
    </UnstyledButton>
  );
}

/**
 * The band of counts under the channel strip: how much has happened with
 * this client, and how long it has been happening.
 *
 * Every figure is a link into the tab that details it, so the band reads
 * as a way in rather than as a scoreboard.
 *
 * @param props - The figures and the tab callback.
 * @returns The performance band.
 */
export function ClientPerformanceStrip({ perf, onOpenTab }: ClientPerformanceStripProps) {
  const { t } = useTranslation('features/clients');
  const isMobile = useIsMobile();
  /* The row only slides where it is a row; on a wide screen it is a grid. */
  const strip = useSlidingStrip(isMobile);

  const figures: Figure[] = [
    { id: 'visits', label: t('detail.performance.visits'), value: String(perf.visits), unit: '', tab: 'visits' },
    { id: 'proposals', label: t('detail.performance.proposals'), value: String(perf.proposals), unit: '', tab: 'proposals' },
    { id: 'interests', label: t('detail.performance.interests'), value: String(perf.interests), unit: '', tab: 'propertiesOfInterest' },
    { id: 'interactions', label: t('detail.performance.interactions'), value: String(perf.interactions), unit: '', tab: 'history' },
    {
      id: 'lastInteraction',
      label: t('detail.performance.lastInteraction'),
      value: perf.daysSinceLastInteraction == null ? '—' : String(perf.daysSinceLastInteraction),
      unit: perf.daysSinceLastInteraction == null ? '' : t('detail.performance.daysAgo'),
      tab: 'history',
    },
    {
      id: 'daysInBase',
      label: t('detail.performance.inBase'),
      value: perf.daysInBase == null ? '—' : String(perf.daysInBase),
      unit: perf.daysInBase == null ? '' : t('detail.performance.days'),
      tab: 'history',
    },
  ];

  return (
    /*
      A grid on a wide screen and a sliding strip on a narrow one, matching
      the listing sheet's numbers row. At 400px the grid wrapped to three
      ragged rows; as a strip the figures stay on one line and the fade says
      which side still has more.
    */
    <div
      ref={strip.ref}
      {...strip.props}
      data-testid="client-performance-strip"
      className={`grid shrink-0 grid-cols-[repeat(auto-fit,minmax(118px,1fr))] gap-(--space-3) rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-card) px-(--space-4) py-(--space-3-5) max-md:flex ${strip.props.className}`}
    >
      {figures.map((figure) => (
        <FigureButton
          key={figure.id}
          figure={figure}
          onOpenTab={onOpenTab}
          wasDragged={strip.wasDragged}
        />
      ))}
    </div>
  );
}
